/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import {
  FadeUpStagger,
  MotionCard,
  MotionButton,
} from "../motion/AdminMotion";
import { adminGlass } from "../../styles/adminGlassTokens";
import TeacherCsvImportDashboard from "./TeacherCsvImportDashboard";
import { assertSecurityLockdown } from "../../utils/assertSecurityLockdown";

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeRole(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "staff") return "teacher";
  if (raw === "teacher") return "teacher";
  if (raw === "admin" || raw === "manager") return "admin";
  return "teacher";
}

function isTeacherOnly(item = {}) {
  return normalizeRole(item.role || item.requestedRole) === "teacher";
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function displayTeacherStatus(item) {
  if (!item) return "inactive";
  if (item.deletedAt) return "inactive";
  if (item.status) return String(item.status).toLowerCase();
  return item.active === false ? "inactive" : "active";
}

function getTeacherName(item) {
  return (
    String(item.displayName || "").trim() ||
    String(item.name || "").trim() ||
    String(item.fullName || "").trim() ||
    String(item.email || "").trim() ||
    "Unnamed teacher"
  );
}

function classSortScore(label) {
  const text = String(label || "").trim().toLowerCase();
  if (text === "nursery") return 0;
  if (text === "reception") return 1;

  const yearMatch = text.match(/^year\s*(\d{1,2})$/i);
  if (yearMatch) return 100 + Number(yearMatch[1]);

  return 1000;
}

function sortClassesForSchool(classes) {
  return [...classes].sort((a, b) => {
    const scoreA = classSortScore(a.label);
    const scoreB = classSortScore(b.label);
    if (scoreA !== scoreB) return scoreA - scoreB;
    return String(a.label || "").localeCompare(String(b.label || ""));
  });
}

function normalizeAssignedClassIdsForSchool(value, classes) {
  const raw = Array.isArray(value) ? value : [];

  const byId = new Map(classes.map((c) => [String(c.id), c.id]));
  const byLabel = new Map(
    classes.map((c) => [String(c.label || "").trim().toLowerCase(), c.id])
  );

  const resolved = raw
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .map((item) => {
      if (byId.has(item)) return byId.get(item);

      const labelMatch = byLabel.get(item.toLowerCase());
      if (labelMatch) return labelMatch;

      return null;
    })
    .filter(Boolean);

  return [...new Set(resolved)];
}

function formatDate(value) {
  try {
    if (!value) return "—";
    if (value?.seconds) {
      return new Date(value.seconds * 1000).toLocaleString([], {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}

function statusTone(status) {
  const value = String(status || "").toLowerCase();

  if (value === "active") {
    return {
      background: "#dcfce7",
      color: "#166534",
      border: "1px solid #bbf7d0",
    };
  }

  if (value === "pending" || value === "approved") {
    return {
      background: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fed7aa",
    };
  }

  return {
    background: "#fff1f2",
    color: "#be123c",
    border: "1px solid #fecdd3",
  };
}

function cardTone(tone) {
  const tones = {
    blue: {
      value: "var(--pp-brand-primary, var(--pp-primary, #4f46e5))",
      bg: "linear-gradient(180deg, rgba(255,255,255,.90), var(--pp-brand-soft, var(--pp-primary-soft, rgba(79,70,229,0.10))))",
      border: "var(--pp-brand-border, var(--pp-primary-border, rgba(79,70,229,0.22)))",
    },
    green: {
      value: "#078348",
      bg: "linear-gradient(180deg, rgba(255,255,255,.86), rgba(236,253,245,.74))",
      border: "rgba(187,247,208,.95)",
    },
    red: {
      value: "#c42424",
      bg: "linear-gradient(180deg, rgba(255,255,255,.86), rgba(254,242,242,.74))",
      border: "rgba(254,202,202,.95)",
    },
    amber: {
      value: "#c44a0a",
      bg: "linear-gradient(180deg, rgba(255,255,255,.86), rgba(255,251,235,.74))",
      border: "rgba(253,230,138,.95)",
    },
    violet: {
      value: "var(--pp-brand-primary, var(--pp-primary, #4f46e5))",
      bg: "linear-gradient(180deg, rgba(255,255,255,.90), var(--pp-brand-soft, var(--pp-primary-soft, rgba(79,70,229,0.10))))",
      border: "var(--pp-brand-border, var(--pp-primary-border, rgba(79,70,229,0.22)))",
    },
  };

  return tones[tone] || tones.blue;
}

export default function TeachersSection() {
  const { profile, user } = useAuth() || {};
  const schoolId = profile?.schoolId || user?.schoolId || "";

  const [classes, setClasses] = useState([]);
  const [staffRows, setStaffRows] = useState([]);
  const [invites, setInvites] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const [form, setForm] = useState({
    name: "",
    email: "",
    assignedClassIds: [],
  });

const [teacherCsvRows, setTeacherCsvRows] = useState([]);
const [teacherCsvFile, setTeacherCsvFile] = useState("");
const [teacherCsvBusy, setTeacherCsvBusy] = useState(false);
const [teacherCsvError, setTeacherCsvError] = useState("");

  const [editingTeacherId, setEditingTeacherId] = useState("");
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    assignedClassIds: [],
    active: true,
  });

  const sendStaffInviteV2 = useMemo(
    () => httpsCallable(functions, "sendStaffInviteV2"),
    []
  );

  useEffect(() => {
    if (!schoolId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubClasses = onSnapshot(
      query(collection(db, "schools", schoolId, "classes")),
      (snap) => {
        const classRows = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            label:
              String(data.name || "").trim() ||
              String(data.className || "").trim() ||
              String(data.year || "").trim() ||
              d.id,
            raw: data,
          };
        });

        setClasses(sortClassesForSchool(classRows));
      },
      (err) => {
        console.error("Classes listener failed:", err);
        setError("Failed to load classes.");
      }
    );

    const unsubStaff = onSnapshot(
      query(collection(db, "schools", schoolId, "staff")),
      (snap) => {
        const rows = snap.docs
          .map((d) => ({
            id: d.id,
            ...(d.data() || {}),
          }))
          .filter(isTeacherOnly);

        setStaffRows(rows);
        setLoading(false);
      },
      (err) => {
        console.error("Staff listener failed:", err);
        setError("Failed to load teachers.");
        setLoading(false);
      }
    );

    const unsubInvites = onSnapshot(
      query(collection(db, "schools", schoolId, "invites")),
      (snap) => {
        const rows = snap.docs
          .map((d) => ({
            id: d.id,
            ...(d.data() || {}),
          }))
          .filter(isTeacherOnly);

        setInvites(rows);
      },
      (err) => {
        console.error("Invites listener failed:", err);
        setError("Failed to load teacher invites.");
      }
    );

    return () => {
      unsubClasses();
      unsubStaff();
      unsubInvites();
    };
  }, [schoolId]);

  const classMap = useMemo(() => {
    const map = {};
    classes.forEach((c) => {
      map[c.id] = c.label;
    });
    return map;
  }, [classes]);

  const teacherRows = useMemo(() => staffRows.filter(isTeacherOnly), [staffRows]);

  const activeTeachers = useMemo(
    () => teacherRows.filter((row) => displayTeacherStatus(row) === "active"),
    [teacherRows]
  );

  const inactiveTeachers = useMemo(
    () => teacherRows.filter((row) => displayTeacherStatus(row) !== "active"),
    [teacherRows]
  );

  const pendingTeacherInvites = useMemo(
    () =>
      invites.filter((item) => {
        const status = String(item.status || "").toLowerCase();
        return status === "pending" || status === "approved";
      }),
    [invites]
  );

  const mergedRows = useMemo(() => {
    const activeEmails = new Set(
      teacherRows.map((item) => normalizeEmail(item.email)).filter(Boolean)
    );

    const pendingInvites = pendingTeacherInvites
      .filter((invite) => !activeEmails.has(normalizeEmail(invite.email)))
      .map((invite) => ({
        ...invite,
        rowKind: "invite",
      }));

    const staff = teacherRows.map((teacher) => ({
      ...teacher,
      rowKind: "staff",
    }));

    return [...staff, ...pendingInvites].sort((a, b) => {
      const aTime = a.updatedAt?.seconds || a.createdAt?.seconds || 0;
      const bTime = b.updatedAt?.seconds || b.createdAt?.seconds || 0;
      return bTime - aTime;
    });
  }, [teacherRows, pendingTeacherInvites]);

  const filteredRows = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();

    return mergedRows.filter((row) => {
      const status = row.rowKind === "invite" ? "pending" : displayTeacherStatus(row);

      if (filter !== "all" && status !== filter) return false;

      if (!q) return true;

      const assigned = safeArray(row.assignedClassIds)
        .map((id) => classMap[id] || id)
        .join(" ");

      const haystack = [
        getTeacherName(row),
        row.email,
        row.role,
        status,
        assigned,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [mergedRows, search, filter, classMap]);

  function selectedClassLabels(ids) {
    return safeArray(ids).map((id) => classMap[id] || id);
  }

  function toggleFormClass(classId) {
    setForm((prev) => {
      const has = prev.assignedClassIds.includes(classId);
      return {
        ...prev,
        assignedClassIds: has
          ? prev.assignedClassIds.filter((id) => id !== classId)
          : [...prev.assignedClassIds, classId],
      };
    });
  }

  function toggleEditClass(classId) {
    setEditForm((prev) => {
      const has = prev.assignedClassIds.includes(classId);
      return {
        ...prev,
        assignedClassIds: has
          ? prev.assignedClassIds.filter((id) => id !== classId)
          : [...prev.assignedClassIds, classId],
      };
    });
  }

  function resetCreateForm() {
    setForm({
      name: "",
      email: "",
      assignedClassIds: [],
    });
  }

  function openEditTeacher(teacher) {
    setEditingTeacherId(teacher.id);
    setEditForm({
      name: getTeacherName(teacher),
      email: String(teacher.email || "").trim(),
      assignedClassIds: normalizeAssignedClassIdsForSchool(
        teacher.assignedClassIds,
        classes
      ),
      active: teacher.active !== false,
    });
    setError("");
    setMessage("");
  }

  function closeEditTeacher() {
    setEditingTeacherId("");
    setEditForm({
      name: "",
      email: "",
      assignedClassIds: [],
      active: true,
    });
  }

function parseCsvLine(line) {
  return line.split(",").map((c) => c.trim());
}

function normalizeCsvHeader(h) {
  return String(h || "")
    .toLowerCase()
    .replace(/\s/g, "")
    .trim();
}

async function handleTeacherCsvSelected(e) {
  const file = e.target.files?.[0];
  if (!file) return;

  try {
    setTeacherCsvBusy(true);
    setTeacherCsvError("");
    setTeacherCsvRows([]);
    setTeacherCsvFile(file.name || "");

    const text = await file.text();

    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    if (lines.length < 2) {
      throw new Error("CSV must include header + rows");
    }

    const headers = parseCsvLine(lines[0]).map(normalizeCsvHeader);

    const requiredHeaders = ["name", "email", "class"];

const validHeaders =
  headers.length === requiredHeaders.length &&
  requiredHeaders.every((h) => headers.includes(h));

if (!validHeaders) {
  throw new Error(
    "Invalid teacher CSV format. Required headers: name,email,class"
  );
}

    const rows = lines.slice(1).map((line, i) => {
      const cells = parseCsvLine(line);
      const row = {};

      headers.forEach((h, idx) => {
        row[h] = cells[idx] || "";
      });

      const name = row.name || row.fullname || "";
      const email = (row.email || "").toLowerCase().trim();
      const rawClass = row.class || row.classname || "";

let matchedClassId = null;
let matchedClassName = "";

if (rawClass) {
  const lower = rawClass.toLowerCase().trim();

  const found = classes.find(
    (c) =>
      c.id.toLowerCase() === lower ||
      c.label.toLowerCase() === lower
  );

  if (found) {
    matchedClassId = found.id;
    matchedClassName = found.label;
  }
}

      const errors = [];

if (!name) errors.push("Missing name");

if (!email) {
  errors.push("Missing email");
} else if (!email.includes("@")) {
  errors.push("Invalid email");
}

if (rawClass && !matchedClassId) {
  errors.push("Class does not exist");
}
return {
  rowNumber: i + 2,
  name,
  email,
  rawClass,
  classId: matchedClassId,
  className: matchedClassName,
  errors,
};
    });

const seenEmails = new Set();

rows.forEach((r) => {
  if (seenEmails.has(r.email)) {
    r.errors.push("Duplicate email in CSV");
  } else {
    seenEmails.add(r.email);
  }
});

const existingEmails = new Set([
  ...staffRows.map((s) => normalizeEmail(s.email)),
  ...invites.map((i) => normalizeEmail(i.email)),
]);

rows.forEach((r) => {
  if (existingEmails.has(r.email)) {
    r.errors.push("Already exists in system");
  }
});

    setTeacherCsvRows(rows);
  } catch (err) {
    setTeacherCsvError(err.message);
  } finally {
    setTeacherCsvBusy(false);
    e.target.value = "";
  }
}

async function importTeachersFromCsv() {
  const importBatchId = `teacher_csv_${Date.now()}`;
  const importedInviteIds = [];

  try {
    setTeacherCsvBusy(true);
    setTeacherCsvError("");
await assertSecurityLockdown(
  schoolId,
  "invites"
);
    const valid = teacherCsvRows.filter((r) => r.errors.length === 0);

    if (!valid.length) {
      throw new Error("No valid rows to import");
    }

    // 🔁 LOOP
    for (const row of valid) {
      const res = await sendStaffInviteV2({
        schoolId,
        name: row.name,
        email: row.email,
        role: "teacher",
        assignedClassIds: row.classId ? [row.classId] : [],
        importBatchId,
      });

      if (res?.data?.inviteId) {
        importedInviteIds.push(res.data.inviteId);
      }
    }

    // ✅ 🔥 THIS IS WHERE IT GOES (RIGHT AFTER LOOP)
    await setDoc(
      doc(db, "schools", schoolId, "auditLogs", importBatchId),
      {
        type: "teacher_csv_import",
        importBatchId,
        createdBy: user?.uid || profile?.uid || "",
        createdByEmail: user?.email || profile?.email || "",
        totalRows: teacherCsvRows.length,
        importedCount: valid.length,
        inviteIds: importedInviteIds,
        status: "completed",
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );

    // ✅ SUCCESS UI
    setMessage("Teachers imported successfully");
    setTeacherCsvRows([]);
    setTeacherCsvFile("");
  } catch (err) {
    setTeacherCsvError(err.message);
  } finally {
    setTeacherCsvBusy(false);
  }
}


  async function handleInviteTeacher(e) {
    e.preventDefault();

    try {
      setSaving(true);
      setError("");
      setMessage("");
      await assertSecurityLockdown(
  schoolId,
  "invites"
);

      const cleanName = String(form.name || "").trim();
      const cleanEmail = normalizeEmail(form.email);

      if (!schoolId) throw new Error("Missing school context.");
      if (!cleanName) throw new Error("Staff name is required.");
      if (!cleanEmail) throw new Error("Staff email is required.");

      await sendStaffInviteV2({
        schoolId,
        name: cleanName,
        email: cleanEmail,
        role: "teacher",
        assignedClassIds: safeArray(form.assignedClassIds),
      });

      await setDoc(
        doc(db, "schools", schoolId, "settings", "school"),
        {
          teachersReady: true,
          onboardingStep: 5,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setMessage("Teacher invite sent successfully.");
      resetCreateForm();
    } catch (err) {
      console.error("Invite teacher failed:", err);
      setError(
        String(err?.message || "")
          .replace(/^firebaseerror:\s*/i, "")
          .replace(/^internal:\s*/i, "")
          .trim() || "Failed to send teacher invite."
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveTeacherEdit() {
    try {
      setSaving(true);
      setError("");
      setMessage("");

      if (!schoolId || !editingTeacherId) {
        throw new Error("Missing teacher record.");
      }

      await updateDoc(doc(db, "schools", schoolId, "staff", editingTeacherId), {
        name: String(editForm.name || "").trim(),
        displayName: String(editForm.name || "").trim(),
        fullName: String(editForm.name || "").trim(),
        role: "teacher",
        assignedClassIds: normalizeAssignedClassIdsForSchool(
          editForm.assignedClassIds,
          classes
        ),
        active: editForm.active !== false,
        status: editForm.active !== false ? "active" : "inactive",
        updatedAt: serverTimestamp(),
      });

      setMessage("Teacher updated successfully.");
      closeEditTeacher();
    } catch (err) {
      console.error("Save teacher edit failed:", err);
      setError(String(err?.message || "").trim() || "Failed to update teacher.");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleTeacherActive(teacher) {
    try {
      setSaving(true);
      setError("");
      setMessage("");

      await updateDoc(doc(db, "schools", schoolId, "staff", teacher.id), {
        active: !(teacher.active !== false),
        status: teacher.active !== false ? "inactive" : "active",
        leftAt: teacher.active !== false ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
      });

      setMessage(
        teacher.active !== false
          ? "Teacher set to inactive."
          : "Teacher reactivated."
      );
    } catch (err) {
      console.error("Toggle teacher active failed:", err);
      setError(
        String(err?.message || "").trim() || "Failed to update teacher status."
      );
    } finally {
      setSaving(false);
    }
  }
const hasErrors = teacherCsvRows.some((r) => r.errors.length > 0);
  return (

    
    <div style={styles.page}>
      {message ? <div style={styles.successBanner}>{message}</div> : null}
      {error ? <div style={styles.errorBanner}>{error}</div> : null}

      <section style={styles.statsGrid}>
        <StatCard label="Total Records" value={teacherRows.length} tone="blue" />
        <StatCard label="Teachers" value={teacherRows.length} tone="green" />
        <StatCard
          label="Pending"
          value={pendingTeacherInvites.length}
          tone="amber"
        />
        <StatCard label="Active" value={activeTeachers.length} tone="green" />
        <StatCard label="Inactive" value={inactiveTeachers.length} tone="red" />
      </section>

<MotionCard style={styles.card} hover>
  <div style={styles.csvHeader}>
    <div>
      <div style={styles.cardEyebrow}>Bulk Import</div>
      <h2 style={styles.cardTitle}>Upload teachers via CSV</h2>
      <p style={styles.cardText}>
        Bulk upload teacher names and emails, then send secure invites.
      </p>
    </div>

    <label style={styles.csvButton}>
      Choose CSV
      <input
        type="file"
        accept=".csv"
        hidden
        onChange={handleTeacherCsvSelected}
      />
    </label>
  </div> {/* ✅ THIS WAS MISSING */}

  {teacherCsvError && (
    <div style={styles.errorBanner}>{teacherCsvError}</div>
  )}

  {teacherCsvRows.length > 0 && (
    <div style={{ marginTop: 16 }}>
      <div style={{ marginBottom: 10, fontWeight: 800 }}>
        {teacherCsvFile} ({teacherCsvRows.length} rows)
      </div>

      <button
  style={styles.primaryButton}
  onClick={importTeachersFromCsv}
  disabled={teacherCsvBusy || hasErrors}
>
  {teacherCsvBusy ? "Importing..." : "Import Teachers"}
</button>
    </div>
  )}

  {teacherCsvRows.length > 0 && (
  <div style={{ marginTop: 20 }}>
    <div style={styles.csvTableHeader}>
      Preview ({teacherCsvRows.length} rows)
    </div>

    <div style={styles.csvTable}>
      {teacherCsvRows.map((row, i) => (
        <div
          key={i}
          style={{
            ...styles.csvRow,
            ...(row.errors.length ? styles.csvRowError : {}),
          }}
        >
          <div style={styles.csvCell}>{row.name}</div>
          <div style={styles.csvCell}>{row.email}</div>

          <div style={styles.csvCell}>
{row.classId ? (
  <span style={styles.badgeSuccess}>
    {row.className}
  </span>
) : row.rawClass ? (
  <span style={styles.badgeWarning}>
    Unknown ({row.rawClass})
  </span>
) : (
  <span style={styles.badgeNeutral}>No class</span>
)}
          </div>

          <div style={styles.csvCell}>
            {row.errors.length > 0 && (
              <span style={styles.badgeError}>
                {row.errors.join(", ")}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
)}
</MotionCard>

<TeacherCsvImportDashboard schoolId={schoolId} />

      <MotionCard style={styles.card} hover>
        <div style={styles.cardEyebrow}>Staff onboarding</div>
        <h2 style={styles.cardTitle}>Invite teacher</h2>
        <p style={styles.cardText}>
          Send a secure invite, assign classes, and prepare teacher access without
          mixing school admins into staff workflows.
        </p>

        <form onSubmit={handleInviteTeacher}>
          <div style={styles.formRow}>
            <Field
              label="Teacher name"
              value={form.name}
              onChange={(value) =>
                setForm((prev) => ({ ...prev, name: value }))
              }
              placeholder="Enter full name"
            />

            <Field
              label="Teacher email"
              value={form.email}
              onChange={(value) =>
                setForm((prev) => ({ ...prev, email: value }))
              }
              placeholder="Enter email address"
            />
          </div>

          <ClassPickerCard
            title="Assign classes"
            subtitle="Choose the classes this teacher should access from day one."
            classes={classes}
            selectedIds={form.assignedClassIds}
            onToggle={toggleFormClass}
          />

          <div style={styles.selectedCard}>
            <div style={styles.selectedLabel}>Selected classes</div>
            <div style={styles.selectedValue}>
              {form.assignedClassIds.length
                ? selectedClassLabels(form.assignedClassIds).join(", ")
                : "None selected"}
            </div>
          </div>

          <MotionButton
            type="submit"
            disabled={saving}
            style={{
              ...styles.primaryButton,
              ...(saving ? styles.primaryButtonDisabled : {}),
            }}
          >
            {saving ? "Sending..." : "Invite Teacher"}
          </MotionButton>
        </form>
      </MotionCard>

      <MotionCard style={styles.card} hover>
        <div style={styles.cardEyebrow}>Team directory</div>
        <h2 style={styles.cardTitle}>Teachers & staff records</h2>
        <p style={styles.cardText}>
          Active teachers and pending teacher invites appear here. School admins
          stay in the separate Admins section.
        </p>

        <div style={styles.toolbar}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, class, or status"
            style={styles.searchInput}
          />

          <div style={styles.filterWrap}>
            {["all", "pending", "active", "inactive"].map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setFilter(item)}
                style={{
                  ...styles.filterBtn,
                  ...(filter === item ? styles.filterBtnActive : {}),
                }}
              >
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={styles.emptyState}>Loading teacher records...</div>
        ) : filteredRows.length === 0 ? (
          <div style={styles.emptyState}>
            No teacher records match this view yet.
          </div>
        ) : (
          <div style={styles.directoryList}>
            <FadeUpStagger delay={0.04} gap={0.04}>
              {filteredRows.map((teacher) => {
                const isInvite = teacher.rowKind === "invite";
                const status = isInvite
                  ? "pending"
                  : displayTeacherStatus(teacher);
                const statusStyle = statusTone(status);
                const classLabels = selectedClassLabels(
                  teacher.assignedClassIds || []
                );

                return (
                  <div key={`${teacher.rowKind}-${teacher.id}`} style={styles.rowCard}>
                    <div style={styles.rowMain}>
                      <div style={styles.avatar}>
                        {getTeacherName(teacher).charAt(0).toUpperCase()}
                      </div>

                      <div style={styles.rowText}>
                        <div style={styles.rowName}>{getTeacherName(teacher)}</div>
                        <div style={styles.rowEmail}>{teacher.email || "—"}</div>
                      </div>
                    </div>

                    <div style={styles.rowMeta}>
                      <div style={styles.metaLabel}>Assigned classes</div>
                      <div style={styles.classInline}>
                        {classLabels.length ? (
                          classLabels.map((label) => (
                            <span key={label} style={styles.smallChip}>
                              {label}
                            </span>
                          ))
                        ) : (
                          <span style={styles.muted}>None</span>
                        )}
                      </div>
                    </div>

                    <div style={styles.rowMeta}>
                      <div style={styles.metaLabel}>Status</div>
                      <span style={{ ...styles.statusPill, ...statusStyle }}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    </div>

                    <div style={styles.rowMeta}>
                      <div style={styles.metaLabel}>Updated</div>
                      <div style={styles.updatedText}>
                        {formatDate(teacher.updatedAt || teacher.createdAt)}
                      </div>
                    </div>

                    <div style={styles.actions}>
                      {!isInvite ? (
                        <>
                          <button
                            type="button"
                            style={styles.actionButton}
                            onClick={() => openEditTeacher(teacher)}
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            style={{
                              ...styles.actionButton,
                              ...styles.dangerButton,
                            }}
                            onClick={() => handleToggleTeacherActive(teacher)}
                            disabled={saving}
                          >
                            {teacher.active !== false
                              ? "Set inactive"
                              : "Reactivate"}
                          </button>
                        </>
                      ) : (
                        <span style={styles.pendingNote}>Awaiting acceptance</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </FadeUpStagger>
          </div>
        )}
      </MotionCard>

      {editingTeacherId ? (
        <div style={styles.modalOverlay}>
          <MotionCard style={styles.modalCard} hover={false}>
            <div style={styles.modalHead}>
              <div>
                <div style={styles.cardEyebrow}>Edit teacher</div>
                <h2 style={styles.cardTitle}>Update teacher record</h2>
              </div>

              <button
                type="button"
                style={styles.closeButton}
                onClick={closeEditTeacher}
              >
                ×
              </button>
            </div>

            <div style={styles.formRow}>
              <Field
                label="Teacher name"
                value={editForm.name}
                onChange={(value) =>
                  setEditForm((prev) => ({ ...prev, name: value }))
                }
                placeholder="Enter full name"
              />

              <Field
                label="Teacher email"
                value={editForm.email}
                onChange={(value) =>
                  setEditForm((prev) => ({ ...prev, email: value }))
                }
                placeholder="Email address"
                disabled
              />
            </div>

            <ClassPickerCard
              title="Assigned classes"
              subtitle="Change the classes this teacher can access."
              classes={classes}
              selectedIds={editForm.assignedClassIds}
              onToggle={toggleEditClass}
            />

            <label style={styles.toggleRow}>
              <div>
                <div style={styles.toggleTitle}>Teacher active</div>
                <div style={styles.toggleText}>
                  Inactive teachers stay in records but lose normal operational
                  access.
                </div>
              </div>

              <input
                type="checkbox"
                checked={editForm.active !== false}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    active: e.target.checked,
                  }))
                }
                style={styles.checkbox}
              />
            </label>

            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={closeEditTeacher}
              >
                Cancel
              </button>

              <MotionButton
                type="button"
                style={styles.primaryButton}
                onClick={handleSaveTeacherEdit}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save changes"}
              </MotionButton>
            </div>
          </MotionCard>
        </div>
      ) : null}
    </div>
  );
}

function StatCard({ label, value, tone = "blue" }) {
  const t = cardTone(tone);

  return (
    <MotionCard
      style={{
        ...styles.statCard,
        background: t.bg,
        border: `1px solid ${t.border}`,
      }}
      hover
    >
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statValue, color: t.value }}>{value}</div>
    </MotionCard>
  );
}

function Field({ label, value, onChange, placeholder, disabled = false }) {
  return (
    <div style={styles.inputBlock}>
      <label style={styles.label}>{label}</label>
      <input
        style={{
          ...styles.input,
          ...(disabled ? styles.inputDisabled : {}),
        }}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function ClassPickerCard({ title, subtitle, classes, selectedIds, onToggle }) {
  return (
    <div style={styles.classPickerCard}>
      <div>
        <h3 style={styles.classPickerTitle}>{title}</h3>
        <p style={styles.classPickerText}>{subtitle}</p>
      </div>

      {classes.length ? (
        <div style={styles.classGrid}>
          {classes.map((item) => {
            const selected = selectedIds.includes(item.id);

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onToggle(item.id)}
                style={{
                  ...styles.classChip,
                  ...(selected ? styles.classChipSelected : {}),
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      ) : (
        <div style={styles.emptyState}>
          No classes yet. Create classes before assigning teachers.
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    display: "grid",
    gap: 22,
  },

  successBanner: {
    padding: 16,
    borderRadius: 18,
    background: "#ecfdf3",
    border: "1px solid #86efac",
    color: "#166534",
    fontWeight: 900,
  },

  errorBanner: {
    padding: 16,
    borderRadius: 18,
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#b91c1c",
    fontWeight: 900,
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 18,
    alignItems: "stretch",
  },

  statCard: {
    minHeight: 132,
    borderRadius: 28,
    padding: "22px 22px",
    boxShadow:
      "0 26px 64px rgba(17,32,66,0.09), inset 0 1px 0 rgba(255,255,255,0.78)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },

  statLabel: {
    fontSize: 12,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    fontWeight: 900,
    color: "#5d6b8a",
    marginBottom: 12,
  },

  statValue: {
    fontSize: 46,
    lineHeight: 1,
    fontWeight: 950,
    letterSpacing: "-0.05em",
  },

  card: {
    ...adminGlass.panel,
    padding: 28,
  },

  cardEyebrow: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    fontWeight: 900,
    color: "#64748b",
    marginBottom: 8,
  },

  cardTitle: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.05,
    letterSpacing: "-0.04em",
    color: "#112042",
    fontWeight: 950,
  },

csvHeader: {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 18,
},

csvButton: {
  border: "none",
  borderRadius: 18,
  padding: "15px 20px",
  background: "var(--pp-brand-gradient, linear-gradient(135deg, var(--pp-brand-primary, var(--pp-primary, #2563eb)), var(--pp-brand-dark, var(--pp-primary-dark, #4f46e5))))",
  color: "#fff",
  fontWeight: 950,
  cursor: "pointer",
  boxShadow: "0 18px 36px rgba(79,70,229,0.18)",
  whiteSpace: "nowrap",
  marginTop: 6,
},

  cardText: {
    marginTop: 10,
    marginBottom: 22,
    color: "#64748b",
    fontSize: 15,
    lineHeight: 1.7,
  },

  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 18,
    marginBottom: 18,
  },

  inputBlock: {
    display: "grid",
    gap: 9,
  },

  label: {
    fontWeight: 900,
    color: "#112042",
    fontSize: 14,
  },

  input: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 18,
    border: "1px solid rgba(219,231,255,0.95)",
    background: "rgba(255,255,255,0.72)",
    padding: "15px 16px",
    fontSize: 15,
    color: "#112042",
    outline: "none",
    fontWeight: 700,
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
  },

  inputDisabled: {
    opacity: 0.62,
    cursor: "not-allowed",
  },

  classPickerCard: {
    borderRadius: 24,
    padding: 20,
    background: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(219,231,255,0.86)",
    boxShadow: "0 10px 24px rgba(17,32,66,0.04)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    marginBottom: 18,
  },

  classPickerTitle: {
    margin: 0,
    fontSize: 22,
    lineHeight: 1.1,
    color: "#112042",
    fontWeight: 950,
  },

  classPickerText: {
    margin: "8px 0 16px",
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.6,
  },

  classGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },

  classChip: {
    borderRadius: 999,
    border: "1px solid rgba(219,231,255,0.95)",
    background: "rgba(255,255,255,0.72)",
    color: "#112042",
    fontWeight: 900,
    padding: "10px 14px",
    cursor: "pointer",
  },

  classChipSelected: {
    background: "var(--pp-brand-gradient, linear-gradient(135deg, var(--pp-brand-primary, var(--pp-primary, #2563eb)), var(--pp-brand-dark, var(--pp-primary-dark, #4f46e5))))",
    color: "#fff",
    border: "1px solid transparent",
    boxShadow: "0 14px 28px rgba(79,70,229,0.18)",
  },

  selectedCard: {
    borderRadius: 22,
    padding: 18,
    background: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(219,231,255,0.86)",
    marginBottom: 18,
  },

  selectedLabel: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontWeight: 900,
    color: "#64748b",
    marginBottom: 8,
  },

  selectedValue: {
    fontWeight: 900,
    color: "#112042",
  },

  primaryButton: {
    border: "none",
    borderRadius: 18,
    padding: "15px 20px",
    background: "var(--pp-brand-gradient, linear-gradient(135deg, var(--pp-brand-primary, var(--pp-primary, #2563eb)), var(--pp-brand-dark, var(--pp-primary-dark, #4f46e5))))",
    color: "#fff",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 18px 36px rgba(79,70,229,0.18)",
  },

  primaryButtonDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
  },

  secondaryButton: {
    border: "1px solid rgba(219,231,255,0.95)",
    borderRadius: 18,
    padding: "15px 20px",
    background: "rgba(255,255,255,0.72)",
    color: "#112042",
    fontWeight: 950,
    cursor: "pointer",
  },

  toolbar: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 12,
    alignItems: "center",
    marginBottom: 18,
  },

  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 18,
    border: "1px solid rgba(219,231,255,0.95)",
    background: "rgba(255,255,255,0.72)",
    padding: "15px 16px",
    fontSize: 15,
    color: "#112042",
    outline: "none",
    fontWeight: 700,
  },

  filterWrap: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },

  filterBtn: {
    borderRadius: 999,
    border: "1px solid rgba(219,231,255,0.95)",
    background: "rgba(255,255,255,0.72)",
    color: "#112042",
    fontWeight: 900,
    padding: "11px 14px",
    cursor: "pointer",
  },

  filterBtnActive: {
    background: "var(--pp-brand-gradient, linear-gradient(135deg, var(--pp-brand-primary, var(--pp-primary, #2563eb)), var(--pp-brand-dark, var(--pp-primary-dark, #4f46e5))))",
    color: "#fff",
    border: "1px solid transparent",
    boxShadow: "0 14px 28px rgba(79,70,229,0.18)",
  },

  directoryList: {
    display: "grid",
    gap: 12,
  },

  rowCard: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 1.1fr) minmax(180px, 1fr) 130px 120px auto",
    gap: 14,
    alignItems: "center",
    padding: 16,
    borderRadius: 24,
    background: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(219,231,255,0.86)",
    boxShadow: "0 10px 24px rgba(17,32,66,0.04)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
  },

  rowMain: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },

  avatar: {
    width: 46,
    height: 46,
    borderRadius: 16,
    display: "grid",
    placeItems: "center",
    background: "var(--pp-brand-gradient, linear-gradient(135deg, var(--pp-brand-primary, var(--pp-primary, #2563eb)), var(--pp-brand-dark, var(--pp-primary-dark, #4f46e5))))",
    color: "#fff",
    fontWeight: 950,
    flexShrink: 0,
  },

  rowText: {
    minWidth: 0,
  },

  rowName: {
    fontSize: 16,
    color: "#112042",
    fontWeight: 950,
    marginBottom: 4,
  },

  rowEmail: {
    color: "#64748b",
    fontSize: 14,
    wordBreak: "break-word",
  },

  rowMeta: {
    minWidth: 0,
  },

  metaLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "#64748b",
    fontWeight: 900,
    marginBottom: 7,
  },

  classInline: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },

  smallChip: {
    borderRadius: 999,
    padding: "6px 9px",
    background: "#eef2ff",
    color: "#4338ca",
    border: "1px solid #c7d2fe",
    fontSize: 12,
    fontWeight: 900,
  },

  muted: {
    color: "#94a3b8",
    fontWeight: 800,
  },

  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "8px 10px",
    fontSize: 12,
    fontWeight: 950,
  },

  updatedText: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 800,
  },

  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
  },

  actionButton: {
    borderRadius: 14,
    border: "1px solid rgba(219,231,255,0.95)",
    background: "rgba(255,255,255,0.72)",
    color: "#2845d9",
    fontWeight: 950,
    padding: "10px 12px",
    cursor: "pointer",
  },

  dangerButton: {
    color: "#be123c",
    border: "1px solid #fecdd3",
    background: "#fff1f2",
  },

  pendingNote: {
    fontSize: 13,
    color: "#c2410c",
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  emptyState: {
    padding: "18px 16px",
    borderRadius: 18,
    background: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(219,231,255,0.86)",
    color: "#6b7a99",
    fontSize: 15,
    fontWeight: 800,
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 80,
    background: "rgba(15,23,42,0.38)",
    display: "grid",
    placeItems: "center",
    padding: 20,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
  },

  modalCard: {
    ...adminGlass.panel,
    width: "min(860px, 96vw)",
    maxHeight: "92vh",
    overflow: "auto",
    padding: 26,
  },

  modalHead: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    marginBottom: 18,
  },

modalCloseBtn: {
  width: 46,
  height: 46,
  borderRadius: 16,
  border: "1px solid rgba(255,255,255,0.7)",
  background: "#ffffff",
  color: "#0f172a",
  fontSize: 24,
  fontWeight: 900,
  cursor: "pointer",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  lineHeight: 1,
  padding: 0,
  flexShrink: 0,
},

  toggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    padding: 16,
    borderRadius: 20,
    background: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(219,231,255,0.86)",
    marginBottom: 18,
    cursor: "pointer",
  },

  toggleTitle: {
    fontWeight: 950,
    color: "#112042",
    marginBottom: 4,
  },

  toggleText: {
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.5,
  },

  checkbox: {
    width: 18,
    height: 18,
    accentColor: "#4F46E5",
  },

  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
  },
  csvTableHeader: {
  fontWeight: 800,
  marginBottom: 10,
},

csvTable: {
  borderRadius: 12,
  overflow: "hidden",
  border: "1px solid #e5e7eb",
},

csvRow: {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr 1fr",
  padding: "10px 12px",
  borderBottom: "1px solid #f1f5f9",
  alignItems: "center",
},

csvRowError: {
  background: "#fff1f2",
},

csvCell: {
  fontSize: 14,
},

badgeSuccess: {
  background: "#dcfce7",
  color: "#166534",
  padding: "4px 8px",
  borderRadius: 8,
  fontWeight: 600,
},

badgeWarning: {
  background: "#fef3c7",
  color: "#92400e",
  padding: "4px 8px",
  borderRadius: 8,
},

badgeError: {
  background: "#fee2e2",
  color: "#991b1b",
  padding: "4px 8px",
  borderRadius: 8,
  fontSize: 12,
},

badgeNeutral: {
  background: "#e5e7eb",
  color: "#374151",
  padding: "4px 8px",
  borderRadius: 8,
},
};
